import { useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import useUiuxStore from 'src/store/useUiuxStore';
import { showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import { gridRefSelectedRowHandler } from 'src/utils/grid/GridHandlerContent';
import { getTQC5020_R01List, getTQC5020_R01SpecList, patchTQC5020_R01SpecList } from './Api_TQC5020_R01';
import { TQC5020_R01HeaderDto } from './Dto_TQC5020_R01';
import { useTQC5020_R01Store } from './Store_TQC5020_R01';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';

interface ContentProps {
	itemGridRef: React.RefObject<AgGridReact<TQC5020_R01HeaderDto> | null>;
}

export const Content_TQC5020_R01 = ({ itemGridRef }: ContentProps) => {
	useFetchActionbarAuth('TQC5020_R01');
	const { setMessage } = useUiuxStore();
	const {
		TQC5020_R01Filter: itemFilter, setTQC5020_R01HeaderList: setItemList, setSelectedTQC5020_R01Header: setSelectedItem,
		setTQC5020_R01DetailListList: setSpecList, isNewItemModalOpen, setIsNewItemModalOpen,
		inspTypeFilter,
	} = useTQC5020_R01Store();

	const fetchSpecList = useCallback(async (item: TQC5020_R01HeaderDto, inspType?: string) => {
		const specResponse = await getTQC5020_R01SpecList(item.itemGroup, item.itemCd, inspType ?? inspTypeFilter);
		setSpecList(specResponse.data.data ?? []);
	}, [setSpecList, inspTypeFilter]);

	const handleItemRowClick = useCallback(async (item: TQC5020_R01HeaderDto) => {
		setSelectedItem(item);
		await fetchSpecList(item);
	}, [setSelectedItem, fetchSpecList]);

	const handleClearItemSelection = useCallback(() => {
		setSelectedItem(undefined);
		setSpecList([]);
	}, [setSelectedItem, setSpecList]);

	const fetchData = useCallback(async () => {
		const response = await getTQC5020_R01List({
			itemGroup: itemFilter.itemGroup || undefined,
			itemCd: itemFilter.itemCd || undefined,
			itemNm: itemFilter.itemNm || undefined,
		});
		const newList = response.data.data ?? [];
		setItemList(newList);

		if (newList.length > 0) {
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}건)`);
			await handleItemRowClick(newList[0]);
			gridRefSelectedRowHandler(itemGridRef, setSelectedItem);
		} else {
			handleClearItemSelection();
			showWarning('조회된 데이터가 없습니다.');
		}
	}, [itemFilter, setItemList, setMessage, handleItemRowClick, handleClearItemSelection, itemGridRef, setSelectedItem]);

	const handleInspTypeChange = useCallback(async (inspType: string) => {
		const { selectedTQC5020_R01Header: currentItem } = useTQC5020_R01Store.getState();
		if (currentItem) await fetchSpecList(currentItem, inspType);
	}, [fetchSpecList]);

	const handleSaveClick = useCallback(async () => {
		const { selectedTQC5020_R01Header: currentItem, TQC5020_R01DetailList: currentSpecList } = useTQC5020_R01Store.getState();

		if (!currentItem) {
			showWarning('품목을 먼저 선택하세요.');
			return;
		}

		const specPayload = currentSpecList.map((row) => ({ ...row, itemCd: currentItem.itemCd }));
		const response = await patchTQC5020_R01SpecList(specPayload);
		if (!response.data.successFlag) return;

		showSuccess('저장이 완료되었습니다.');
		await fetchSpecList(currentItem);
	}, [fetchSpecList]);

	const handleOpenNewItemModal = useCallback(() => {
		const { selectedTQC5020_R01Header: currentItem } = useTQC5020_R01Store.getState();
		if (!currentItem) {
			showWarning('품목을 먼저 선택하세요.');
			return;
		}
		setIsNewItemModalOpen(true);
	}, [setIsNewItemModalOpen]);

	const handleNewItemSaved = useCallback(async () => {
		const { selectedTQC5020_R01Header: currentItem } = useTQC5020_R01Store.getState();
		if (currentItem) await fetchSpecList(currentItem);
	}, [fetchSpecList]);

	return {
		fetchData,
		handleItemRowClick,
		handleClearItemSelection,
		handleInspTypeChange,
		handleSaveClick,
		handleOpenNewItemModal,
		isNewItemModalOpen,
		setIsNewItemModalOpen,
		handleNewItemSaved,
	};
};
